import { LOCALE_NAMES } from "@repo/shared";
import type { LocaleCode, PdfTemplateWithFlow } from "@repo/shared/types";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogCancel,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useCreatePdfTemplate } from "@/lib/hooks/use-pdf-templates";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./pdf-template-dialog.i18n";

type Flow = {
  id: string;
  name: string;
  language?: string;
};

type PdfTemplateDialogProps = {
  open: boolean;
  template: PdfTemplateWithFlow | null;
  flows: Flow[];
  supportedLanguages: string[];
  defaultLanguage: string;
  onClose: () => void;
};

export function PdfTemplateDialog({
  open,
  template: _template,
  flows,
  supportedLanguages,
  defaultLanguage,
  onClose,
}: PdfTemplateDialogProps) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    language: defaultLanguage,
    flowId: "",
  });
  const [error, setError] = useState<string | null>(null);
  const createMutation = useCreatePdfTemplate();
  const t = useT(T);

  // Filter flows based on selected language
  const filteredFlows = useMemo(() => {
    return flows.filter((flow) => flow.language === formData.language);
  }, [flows, formData.language]);

  useEffect(() => {
    // Find first flow matching the selected language
    const firstMatchingFlow = flows.find((f) => f.language === defaultLanguage);
    setFormData({
      name: "",
      description: "",
      language: defaultLanguage,
      flowId: firstMatchingFlow?.id || "",
    });
    setError(null);
  }, [open, flows, defaultLanguage]);

  // When language changes, reset flowId if current flow doesn't match
  const handleLanguageChange = (newLanguage: string) => {
    const flowsForLanguage = flows.filter((f) => f.language === newLanguage);
    const currentFlowMatchesLanguage = flowsForLanguage.some((f) => f.id === formData.flowId);

    setFormData({
      ...formData,
      language: newLanguage,
      flowId: currentFlowMatchesLanguage ? formData.flowId : flowsForLanguage[0]?.id || "",
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.flowId) {
      setError(t.pleaseSelectFlow);
      return;
    }

    try {
      const result = await createMutation.mutateAsync({
        name: formData.name,
        description: formData.description || undefined,
        flowId: formData.flowId,
        language: formData.language,
        isActive: true,
      });

      if (result.id) {
        navigate(`/settings/pdf-templates/${result.id}`);
      }

      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t.failedToCreate);
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t.createPdfTemplateTitle}</DialogTitle>
          <DialogDescription>{t.createDialogDescription}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            {error && (
              <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="name">
                {t.name} <span className="text-destructive">*</span>
              </Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={t.namePlaceholder}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">{t.description}</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder={t.descriptionPlaceholder}
                rows={3}
              />
            </div>

            {/* Language selector */}
            <div className="space-y-2">
              <Label htmlFor="language">
                {t.language} <span className="text-destructive">*</span>
              </Label>
              <Select value={formData.language} onValueChange={handleLanguageChange}>
                <SelectTrigger>
                  <SelectValue placeholder={t.selectLanguage} />
                </SelectTrigger>
                <SelectContent>
                  {supportedLanguages.map((lang) => (
                    <SelectItem key={lang} value={lang}>
                      {LOCALE_NAMES[lang as LocaleCode] || lang}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Flow selector - filtered by language */}
            <div className="space-y-2">
              <Label htmlFor="flowId">
                {t.flow} <span className="text-destructive">*</span>
              </Label>
              {filteredFlows.length > 0 ? (
                <Select
                  value={formData.flowId}
                  onValueChange={(value) => setFormData({ ...formData, flowId: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t.selectFlow} />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredFlows.map((flow) => (
                      <SelectItem key={flow.id} value={flow.id}>
                        {flow.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-sm text-muted-foreground">{t.noFlowsForLanguage}</p>
              )}
            </div>
          </div>

          <DialogFooter>
            <DialogCancel disabled={createMutation.isPending}>{t.cancel}</DialogCancel>
            <Button type="submit" loading={createMutation.isPending}>
              {t.create}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
